import React, { useCallback, useMemo, useState } from 'react';
import { Button, Checkbox, DatePicker, Form, List, message, Popconfirm, Spin, TimePicker } from 'antd-v5';
import dayjs from 'dayjs';
import { format } from 'date-fns';
import { useQuery } from 'react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { SettingsContent } from '../../../components/SettingsContent';
import DescriptionBox from '../../../../../../components/Layout/DescriptionBox';
import { TimezoneDropdownV2 } from '../../../../../CustomizedAntDesign/TimezoneDropdownV2';
import { deleteStoreHoursByDate, getStoreHoursByDate, saveStoreHoursByDate } from '../../../../../../services/stores';

export const StoreHours = ({ daysOfWeek, form, storeId }) => {
  const [saving, setSaving] = useState(false);
  const [openAdd, setOpenAdd] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [availability, setAvailability] = useState({
    date: null,
    openTime: null,
    closeTime: null,
    closed: false,
  });

  const {
    data: availabilityData,
    isFetching: isFetchingAvailability,
    refetch: refetchAvailability,
  } = useQuery(
    ['store-availability', storeId, page, pageSize],
    async () => {
      try {
        return getStoreHoursByDate({
          storeId,
          query: { limit: pageSize, offset: pageSize * (page - 1) },
        });
      } catch (e) {
        console.log('Error fetching store availability', e);
        return [];
      }
    },
    {
      enabled: !!storeId,
      refetchOnWindowFocus: false,
      initialData: {
        total: 0,
        limit: 10,
        offset: 0,
        data: [],
      },
    },
  );

  const handleOpenNew = useCallback(() => {
    setOpenAdd(true);
    setAvailability({
      date: null,
      openTime: null,
      closeTime: null,
      closed: false,
    });
  }, []);

  const handleEdit = useCallback(item => {
    setOpenAdd(true);
    setAvailability({
      date: dayjs(item.date),
      openTime: item.openTime
        ? dayjs()
          .set('hour', Math.floor(item.openTime / 100))
          .set('minute', item.openTime % 100)
        : null,
      closeTime: item.closeTime
        ? dayjs()
          .set('hour', Math.floor(item.closeTime / 100))
          .set('minute', item.closeTime % 100)
        : null,
      closed: !item.openTime && !item.closeTime,
    });
  }, []);

  const handleCancel = useCallback(() => {
    setOpenAdd(false);
    setAvailability({
      date: null,
      openTime: null,
      closeTime: null,
      closed: false,
    });
  }, []);

  const handleSave = useCallback(async () => {
    setSaving(true);
    const data = {
      date: availability.date.format('YYYY-MM-DD'),
      openTime: availability.openTime && !availability.closed ? availability.openTime.format('Hmm') : null,
      closeTime: availability.closeTime && !availability.closed ? availability.closeTime.format('Hmm') : null,
    };
    try {
      await saveStoreHoursByDate({ storeId, data });
      message.success('Date saved');
      setOpenAdd(false);
      setAvailability({
        date: null,
        openTime: null,
        closeTime: null,
        closed: false,
      });
      refetchAvailability();
    } catch (e) {
      console.error(e);
      message.error('There was an error saving the availability');
    } finally {
      setSaving(false);
    }
  }, [availability]);

  const handleDelete = useCallback(async date => {
    try {
      setSaving(true);
      await deleteStoreHoursByDate({ storeId, date });
      message.success('Store hour deleted');
      refetchAvailability();
    } catch (e) {
      console.error(e);
      message.error('There was an error deleting the store hour');
    } finally {
      setSaving(false);
    }
  }, []);

  const disabledDates = useMemo(() => {
    if (availabilityData.data) {
      return availabilityData.data.map(item => dayjs(item.date));
    }
  }, [availabilityData]);

  return (
    <SettingsContent
      icon={<FontAwesomeIcon icon={['far', 'calendar-clock']} />}
      title="STORE HOURS"
      description="Store hours control when auto messages are allowed to go out, and when your auto-responder will send replies to inbound messages."
    >
      <DescriptionBox
        className="w-100-P"
        title="Store Time Zone"
        description="What is the timezone your store is located in?"
        input={
          <Form.Item name="timezone" style={{ marginBottom: 0 }}>
            <TimezoneDropdownV2 style={{ width: '267px' }} placeholder="Select Timezone" />
          </Form.Item>
        }
      />
      {daysOfWeek.map(day => (
        <StoreHoursItem key={day.day} label={day.label} day={day.day} form={form} />
      ))}
      <div className="w-100-P">
        <Spin spinning={isFetchingAvailability}>
          <DescriptionBox
            className="w-100-P m-top-30"
            title="Hours on Specific Dates"
            description="Manage dates that your store is closed or has limited hours outside of your normal business hours."
            input={
              <Button disabled={openAdd} type="primary" onClick={handleOpenNew}>
                Add a Date
              </Button>
            }
          />
          {openAdd && (
            <div className="flex-row-spacebetween-center w-100-P m-btm-24">
              <div className="flex-row-center gap-16">
                <DatePicker
                  value={availability.date}
                  onChange={date => setAvailability({ ...availability, date })}
                  disabledDate={current => {
                    return disabledDates?.some(date => date.isSame(current, 'day')) || current < new Date().setDate(new Date().getDate() - 1);
                  }}
                />
                <TimePicker.RangePicker
                  disabled={availability.closed}
                  style={{ width: '320px' }} m
                  format="hh:mma"
                  value={[availability.openTime, availability.closeTime]}
                  onChange={([open, close]) => setAvailability({ ...availability, openTime: open, closeTime: close })}
                />
                <Checkbox
                  checked={availability.closed}
                  onChange={e => setAvailability({ ...availability, closed: e.target.checked })}
                >
                  Closed
                </Checkbox>
              </div>
              <div className="flex-row-center gap-16">
                <Button
                  type="primary"
                  onClick={handleSave}
                  disabled={
                    !availability.date ||
                    (!availability.closed && (!availability.openTime || !availability.closeTime)) ||
                    saving
                  }
                  loading={saving}
                >
                  Save
                </Button>
                <Button onClick={handleCancel} disabled={saving}>
                  Cancel
                </Button>
              </div>
            </div>
          )}
          <List
            dataSource={availabilityData?.data}
            renderItem={item => (
              <AvailabilityItem key={item.date} {...item} handleEdit={handleEdit} handleDelete={handleDelete} />
            )}
            pagination={{
              current: page,
              pageSize,
              total: availabilityData?.total,
              onChange: (p, ps) => {
                setPage(p);
                setPageSize(ps);
              },
              showSizeChanger: { showSearch: false },
              pageSizeOptions: ['5', '10', '20', '50'],
              showTotal: t => `Total ${t} items`,
            }}
          />
        </Spin>
      </div>
    </SettingsContent>
  );
};

const AvailabilityItem = ({ closeTime, openTime, date, handleEdit, handleDelete }) => {
  const label = () => {
    if (openTime !== null && closeTime !== null) {
      // the openTime and closeTime come as a number 900 or 1700, we need to tranform it as HH:mm am/pm
      const openTimeFormatted = format(new Date().setHours(Math.floor(openTime / 100), openTime % 100), 'h:mmaaa');
      const closeTimeFormatted = format(new Date().setHours(Math.floor(closeTime / 100), closeTime % 100), 'h:mmaaa');
      return `${openTimeFormatted} - ${closeTimeFormatted}`;
    }
    return 'Closed';
  };

  return (
    <div className="flex-row-spacebetween-center w-100-P m-top-10">
      <div className="flex-row-center gap-16">
        <span>{dayjs(date).format('MMMM DD, YYYY')}</span>
        <span>{label()}</span>
      </div>
      <div className="flex-row-center gap-16">
        <Button
          onClick={() =>
            handleEdit({
              closeTime,
              openTime,
              date,
            })
          }
        >
          Edit
        </Button>
        <Popconfirm onConfirm={() => handleDelete(date)} title="Are you sure you want to delete this availability?">
          <Button danger>Delete</Button>
        </Popconfirm>
      </div>
    </div>
  );
};

const StoreHoursItem = ({ label, day, form }) => {
  const checkbox = Form.useWatch(['open_hours', day], form);
  return (
    <div className="flex-row-spacebetween-center w-100-P">
      <Form.Item name={['open_hours', day]} style={{ marginBottom: 8 }} valuePropName="checked">
        <Checkbox>{label}</Checkbox>
      </Form.Item>
      <Form.Item name={['open_hours', `${day}_hours`]} style={{ marginBottom: 8 }}>
        <TimePicker.RangePicker
          disabled={!checkbox}
          style={{ width: '320px' }}
          format="hh:mma"
          placeholder={['Open', 'Close']}
        />
      </Form.Item>
    </div>
  );
};
